/**
 * Čo update s výpočtom spraví — kým sa to nestalo (KOLO 118).
 *
 * User: *„daj mi možnosť ako keby reupload ale update"*, a k tomu
 * rozhodnutie z toho istého kola: **na každý ručne upravený riadok sa
 * pýtať**, s predvoľbou nechať jeho verziu.
 *
 * Preto sú tu tri sekcie a štvrtá navyše: pribudne / zmení sa / zmizne
 * a **Tvoje úpravy**, kde sú riadky, ktoré zmenil user aj TopSolid.
 * Zaškrtnutie je na riadok a znamená „ber aj sporné polia z exportu";
 * nesporné zmeny sa preberú aj bez neho, inak by jedna sporná poznámka
 * zablokovala aj opravený rozmer.
 *
 * Slovník stavov je zhodný s „Porovnať výpočty"
 * (`lib/porovnanieVypoctov.ts`) — dve porovnania nemajú mať dve sady slov.
 *
 * Kovanie (KOLO 220) má vlastnú sekciu s fajkou na každom riadku:
 * pribudne (Pridať), nie je v exporte (Zmazať — ručne pridané predvolene
 * nie) a sporný počet (Prepísať). User: *„ty musíš aj kontrolovať, čo
 * máme my v tej zostave"*.
 */
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { vDruhu } from "@/lib/import/druhVarianty";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  DielecZmena,
  KovanieVolby,
  KovanieZmena,
  UpdateNahlad,
} from "@/api/hooks/useTopsolidUpdate";

/** Ľudské názvy polí — `x_mm` nikomu nič nepovie. */
const POLE_SK: Record<string, string> = {
  nazov_strojovy: "Názov",
  pocet_ks: "Počet ks",
  x_mm: "Dĺžka",
  y_mm: "Šírka",
  z_mm: "Hrúbka",
  smer_vlakien: "Smer vlákien",
  operacia: "Výrobný postup",
  material_id: "Materiál",
  hrana_x1_id: "Hrana X1",
  hrana_x2_id: "Hrana X2",
  hrana_y1_id: "Hrana Y1",
  hrana_y2_id: "Hrana Y2",
  // Hrany mimo strán podľa neolepeného dielca (zaoblený roh, KOLO 173)
  hrany_navyse_x_mm: "Hrany mimo strán X",
  hrany_navyse_y_mm: "Hrany mimo strán Y",
};

function popisZmeny(z: DielecZmena): string {
  return z.zmeny
    .map((p) => `${POLE_SK[p.pole] ?? p.pole}: ${p.zo ?? "—"} → ${p.na ?? "—"}`)
    .join(" · ");
}

function Sekcia({
  nadpis,
  popis,
  dielce,
}: {
  nadpis: string;
  popis: string;
  dielce: DielecZmena[];
}): JSX.Element | null {
  if (dielce.length === 0) return null;
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium">
        {nadpis} <span className="text-muted-foreground">({dielce.length})</span>
      </h3>
      <p className="text-xs text-muted-foreground">{popis}</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[90px]">Zostava</TableHead>
            <TableHead className="w-[200px]">Dielec</TableHead>
            <TableHead>Čo sa mení</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dielce.map((d) => (
            <TableRow key={`${d.zostava_kod}|${d.kod}`}>
              <TableCell className="font-mono text-xs">
                {d.zostava_kod}
              </TableCell>
              <TableCell className="text-xs">
                {d.nazov}
                <span className="ml-1 font-mono text-muted-foreground">
                  {d.kod}
                </span>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {popisZmeny(d) || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

/** Kľúč fajky riadku kovania — pribudnuté nemá id riadku v zákazke. */
function klucKovania(k: KovanieZmena): string {
  return k.kluc ?? k.dielec_id ?? `${k.zostava_kod}|${k.material_id}`;
}

function predvoleneKovanie(kovanie: KovanieZmena[]): Record<string, boolean> {
  return Object.fromEntries(kovanie.map((k) => [klucKovania(k), k.predvolene]));
}

function ks(pocet: number | null, mj: string): string {
  return pocet == null ? "—" : `${pocet} ${mj}`;
}

/** Čo sa s riadkom kovania stane — a fajka, keď je o čom rozhodnúť. */
function popisKovania(k: KovanieZmena): { co: string; fajka: string | null } {
  if (k.stav === "novy") {
    return {
      co: k.konflikt
        ? `pribudne ${ks(k.pocet_na, k.mj)} — zmazal si ho, TopSolid ho zmenil`
        : `pribudne ${ks(k.pocet_na, k.mj)}`,
      fajka: "Pridať",
    };
  }
  if (k.stav === "zmizol") {
    return {
      co: k.rucne
        ? `${ks(k.pocet_zo, k.mj)} — pridal si ho ručne, v exporte nie je`
        : `${ks(k.pocet_zo, k.mj)} — v exporte nie je`,
      fajka: "Zmazať",
    };
  }
  return {
    co: k.konflikt
      ? `tvoj počet ${ks(k.pocet_zo, k.mj)}, TopSolid ${ks(k.pocet_na, k.mj)}`
      : `${ks(k.pocet_zo, k.mj)} → ${ks(k.pocet_na, k.mj)}`,
    // Nesporná oprava počtu sa preberie ako pri dielcoch
    fajka: k.konflikt ? "Prepísať" : null,
  };
}

const PORADIE_KOVANIA: Record<string, number> = { novy: 0, zmeneny: 1, zmizol: 2 };

function SekciaKovania({
  kovanie,
  volby,
  setVolby,
}: {
  kovanie: KovanieZmena[];
  volby: Record<string, boolean>;
  setVolby: (f: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
}): JSX.Element | null {
  if (kovanie.length === 0) return null;
  const riadky = [...kovanie].sort(
    (a, b) =>
      (PORADIE_KOVANIA[a.stav] ?? 3) - (PORADIE_KOVANIA[b.stav] ?? 3) ||
      Number(a.rucne) - Number(b.rucne),
  );
  return (
    <section className="space-y-1" data-testid="kovanie-zmeny">
      <h3 className="text-sm font-medium">
        Kovanie <span className="text-muted-foreground">({kovanie.length})</span>
      </h3>
      <p className="text-xs text-muted-foreground">
        Kovanie zostáv porovnané s exportom — sety (záves + podložka) ako
        v kusovníku. Čo nechceš zmazať či pridať, odškrtni.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[80px]" />
            <TableHead className="w-[150px]">Zostava</TableHead>
            <TableHead className="w-[220px]">Kovanie</TableHead>
            <TableHead>Čo sa stane</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {riadky.map((k) => {
            const kluc = klucKovania(k);
            const { co, fajka } = popisKovania(k);
            return (
              <TableRow key={kluc}>
                <TableCell className="text-xs">
                  {fajka ? (
                    <label className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        aria-label={`${fajka} ${k.nazov} (${k.zostava_kod})`}
                        checked={Boolean(volby[kluc])}
                        onChange={(e) =>
                          setVolby((prev) => ({ ...prev, [kluc]: e.target.checked }))
                        }
                      />
                      {fajka}
                    </label>
                  ) : null}
                </TableCell>
                <TableCell className="text-xs">
                  <span className="font-mono">{k.zostava_kod}</span>{" "}
                  <span className="text-muted-foreground">{k.zostava_nazov}</span>
                </TableCell>
                <TableCell className="text-xs">{k.nazov}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{co}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
}

/** Voľby okna → `kovanie_json` + sporné počty do `prepisat`. */
function volbyKovania(
  kovanie: KovanieZmena[],
  volby: Record<string, boolean>,
): { kovanie: KovanieVolby; prepisat: string[] } {
  const zapnute = (k: KovanieZmena) => Boolean(volby[klucKovania(k)]);
  return {
    kovanie: {
      zmazat: kovanie
        .filter((k) => k.stav === "zmizol" && k.dielec_id && zapnute(k))
        .map((k) => k.dielec_id as string),
      nepridat: kovanie
        .filter((k) => k.stav === "novy" && k.kluc && !zapnute(k))
        .map((k) => k.kluc as string),
    },
    prepisat: kovanie
      .filter((k) => k.stav === "zmeneny" && k.konflikt && k.dielec_id && zapnute(k))
      .map((k) => k.dielec_id as string),
  };
}

export function UpdateZmenyDialog({
  open,
  onOpenChange,
  nahlad,
  bezi,
  onPotvrd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nahlad: UpdateNahlad | null;
  bezi: boolean;
  /** Dostane id riadkov, pri ktorých si user odklikol prepis, a voľby
   *  kovania (vždy, aj prázdne — bez nich server kovanie nechá). */
  onPotvrd: (prepisat: string[], kovanie: KovanieVolby) => void;
}): JSX.Element {
  const [prepisat, setPrepisat] = useState<Record<string, boolean>>({});
  const kovanie = nahlad?.kovanie ?? [];
  const [volbyKov, setVolbyKov] = useState<Record<string, boolean>>(() =>
    predvoleneKovanie(kovanie),
  );

  // Nový náhľad = nové rozhodnutia. Ponechať staré zaškrtnutia by
  // znamenalo prepísať riadok, ktorý si v tomto náhľade ani nevidel.
  useEffect(() => {
    setPrepisat({});
    setVolbyKov(predvoleneKovanie(nahlad?.kovanie ?? []));
  }, [nahlad]);

  const dielce = nahlad?.dielce ?? [];
  const konfliktne = dielce.filter((d) => d.konflikt);
  const nove = dielce.filter((d) => d.stav === "novy");
  const zmenene = dielce.filter((d) => d.stav === "zmeneny" && !d.konflikt);
  const zmiznute = dielce.filter((d) => d.stav === "zmizol");
  // Mená zostáv z Tandem Zostáv (KOLO 146) sú tiež zmena — update, ktorý
  // len premenuje, nesmie hlásiť „nie je čo aktualizovať"
  const premenovane = nahlad?.premenovane_zostavy ?? [];
  const niecoJe =
    Boolean(nahlad) && (dielce.length > 0 || premenovane.length > 0 || kovanie.length > 0);
  // Tá istá zostava dvakrát (KOLO 221) — párovanie by vzalo poslednú,
  // server aktualizáciu nepustí
  const dvojite = nahlad?.dvojite_zostavy ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] w-[96vw] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>Zmeny z opraveného exportu</DialogTitle>
          <DialogDescription>
            {dvojite.length > 0
              ? "Najprv treba zlúčiť zostavu, ktorá je v zákazke dvakrát."
              : nahlad
              ? `Pribudne ${nahlad.novych} · zmení sa ${nahlad.zmenenych} · zmizne ${nahlad.zmiznutych}` +
                (premenovane.length > 0 ? ` · premenuje sa zostáv ${premenovane.length}` : "") +
                (kovanie.length > 0 ? ` · kovanie ${kovanie.length}` : "")
              : "Čítam export…"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
          {/* AA6246: dva behy staršej aktualizácie rozdelili skrinku do Q a R
              s rovnakým menom a druhý dal chýbajúce dielce do poslednej */}
          {dvojite.length > 0 ? (
            <p
              role="alert"
              data-testid="dvojite-zostavy"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              <strong>
                V zákazke je dvakrát tá istá zostava:{" "}
                {dvojite.map((d) => `${d.urceni} (${d.kody.join(", ")})`).join("; ")}.
              </strong>{" "}
              Aktualizácia nevie, do ktorej patrí — presuň dielce do jednej
              (Vystrihnúť / Vložiť), prázdnu zmaž a aktualizuj znova.
            </p>
          ) : null}

          {/* Zoznamy pri dvojitej zostave neukazovať — opisovali by párovanie
              s poslednou zostavou (napr. kovanie Q „zmizne", do R „pribudne"),
              ktoré server aj tak nepustí */}
          {dvojite.length > 0 ? null : (
            <>
            {/* Poistka (KOLO 203): AA6262_obyvacka vzatá ako opravený export
                skrinky zmazala skoro všetky dielce — vyzerá to ako iná časť */}
            {nahlad?.zmizne_vacsina ? (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                <strong>
                  Zmizne {nahlad.zmiznutych} z {nahlad.povodnych} dielcov
                </strong>{" "}
                — nie je to iná časť zákazky? Zavri náhľad a pošli export z Tandem
                Zostáv znova: v okne vyber <em>Pridať ako novú časť</em>.
              </p>
            ) : null}

            {nahlad && !niecoJe ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Export sa od posledného importu nezmenil — nie je čo
                aktualizovať.
              </p>
            ) : null}

            {konfliktne.length > 0 ? (
              <section className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-950">
                <h3 className="text-sm font-medium">
                  Tvoje úpravy{" "}
                  <span className="text-muted-foreground">
                    ({konfliktne.length})
                  </span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  Tieto riadky si menil ty <strong>aj</strong> TopSolid.
                  Nechávam tvoju verziu; zaškrtni ten, pri ktorom chceš radšej
                  export. Ostatné zmeny na riadku sa preberú tak či tak.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">Prepísať</TableHead>
                      <TableHead className="w-[90px]">Zostava</TableHead>
                      <TableHead className="w-[200px]">Dielec</TableHead>
                      <TableHead>Sporné</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {konfliktne.map((d) => (
                      <TableRow key={d.dielec_id ?? d.kod}>
                        <TableCell>
                          <input
                            type="checkbox"
                            aria-label={`Prepísať ${d.kod}`}
                            checked={Boolean(
                              d.dielec_id && prepisat[d.dielec_id],
                            )}
                            onChange={(e) =>
                              d.dielec_id &&
                              setPrepisat((prev) => ({
                                ...prev,
                                [d.dielec_id as string]: e.target.checked,
                              }))
                            }
                          />
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {d.zostava_kod}
                        </TableCell>
                        <TableCell className="text-xs">
                          {d.nazov}
                          <span className="ml-1 font-mono text-muted-foreground">
                            {d.kod}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {d.zmeny
                            .filter((p) => p.konflikt)
                            .map(
                              (p) =>
                                `${POLE_SK[p.pole] ?? p.pole}: ${p.zo ?? "—"} → ${p.na ?? "—"}`,
                            )
                            .join(" · ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </section>
            ) : null}

            {premenovane.length > 0 ? (
              <section className="space-y-1" data-testid="premenovane-zostavy">
                <h3 className="text-sm font-medium">
                  Premenuje sa{" "}
                  <span className="text-muted-foreground">({premenovane.length})</span>
                </h3>
                <p className="text-xs text-muted-foreground">
                  Mená zostáv z Tandem Zostáv: Názov = meno z TopSolidu, Popis = názov
                  uzla (URCENI).
                </p>
                <ul className="space-y-0.5 text-sm">
                  {premenovane.map((z) => (
                    <li key={z.zostava_id}>
                      <span className="font-mono">{z.kod}</span>: {z.nazov_zo} → {z.nazov_na}
                      {z.popis_na && z.popis_na !== z.popis_zo ? (
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          (popis {z.popis_na})
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <Sekcia
              nadpis="Zmení sa"
              popis="Opravy z TopSolidu, ktorých si sa nedotkol — preberú sa."
              dielce={zmenene}
            />
            <Sekcia
              nadpis="Pribudne"
              popis={`Dielce, ktoré v exporte sú a ${vDruhu(nahlad?.co)} ešte nie.`}
              dielce={nove}
            />
            <Sekcia
              nadpis="Zmizne"
              popis="V exporte už nie sú. Zmažú sa mäkko — dajú sa obnoviť."
              dielce={zmiznute}
            />

            {nahlad && nahlad.nove_zostavy.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Nové zostavy: {nahlad.nove_zostavy.join(", ")}
              </p>
            ) : null}
            {/* Do KOLA 203 sa počítali, ale neukazovali — zmiznutá zostava je
                najrýchlejší signál, že export patrí inej časti zákazky */}
            {nahlad && nahlad.zmiznute_zostavy.length > 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="zmiznute-zostavy">
                Zostavy, ktoré v exporte nie sú: {nahlad.zmiznute_zostavy.join(", ")}
              </p>
            ) : null}

            <SekciaKovania kovanie={kovanie} volby={volbyKov} setVolby={setVolbyKov} />
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Zrušiť
          </Button>
          <Button
            disabled={!niecoJe || bezi || dvojite.length > 0}
            onClick={() => {
              const kov = volbyKovania(kovanie, volbyKov);
              onPotvrd(
                [
                  ...Object.entries(prepisat)
                    .filter(([, v]) => v)
                    .map(([k]) => k),
                  ...kov.prepisat,
                ],
                kov.kovanie,
              );
            }}
          >
            {bezi ? "Aktualizujem…" : "Aktualizovať"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
